import {
  getRenderer,
  registerObjects,
  registerRenderers,
  type AssetRenderer,
  type ObjectDefinition,
  type StudioAssetRecord,
} from '../../engine';

/**
 * Register an uploaded asset as a placeable object: a project-scoped
 * ObjectDefinition (bounds from the extracted GLB bounds) whose renderer wraps
 * the engine's built-in `gltf_model` renderer in a group that base-aligns and
 * XZ-centres the model using its extracted bounds, so an off-origin model lines
 * up with its placement gizmo and selection box. Idempotent per asset id.
 */
export function registerProjectAsset(asset: StudioAssetRecord): void {
  // Only GLB models are placeable scene objects; other kinds (textures,
  // audio) are consumed by their own modes.
  if (asset.kind !== 'glb') return;
  const definition: ObjectDefinition = {
    id: asset.id,
    name: asset.name,
    category: asset.category,
    description: `Uploaded model: ${asset.name}`,
    bounds: { size: asset.bounds.size },
    defaultParams: { url: asset.downloadUrl, scale: 1 },
    editor: {
      pivot: 'base-center',
      snap: 0.1,
      tags: ['upload', 'model'],
      thumbnailOverride: asset.thumbnailUrl,
    },
  };
  registerObjects([definition]);

  const gltfRenderer = getRenderer('gltf_model');
  if (!gltfRenderer) return;

  // Translate so the model's bounding-box base sits at y=0 and its XZ centre at
  // the origin (matching base-center pivot). center = box-centre offset; the
  // offset is scaled by the runtime `scale` param so alignment holds at any scale
  // (the inner gltf renderer applies that same scale to the model).
  const [cx, cy, cz] = asset.bounds.center;
  const sizeY = asset.bounds.size[1];
  const baseOffset: [number, number, number] = [-cx, -(cy - sizeY / 2), -cz];

  const centeredRenderer: AssetRenderer = (props) => {
    const rawScale = props.instance.params?.scale;
    const scale = typeof rawScale === 'number' && Number.isFinite(rawScale) ? rawScale : 1;
    return (
      <group position={[baseOffset[0] * scale, baseOffset[1] * scale, baseOffset[2] * scale]}>
        {gltfRenderer(props)}
      </group>
    );
  };
  registerRenderers({ [asset.id]: centeredRenderer });
}

export function registerProjectAssets(assets: StudioAssetRecord[]): void {
  for (const asset of assets) registerProjectAsset(asset);
}
